import api from '../../../services/api';
import {
  setOrderItems,
  setFilteredOrderItems,
  setLoading,
  setError,
  setPagination,
  setFilters,
  clearFilters,
  setKpiMetrics
} from '../reducers';
import dayjs from 'dayjs';

// Hàm lấy danh sách các mục đơn hàng với phân trang và filters
export const fetchOrderItems = (page = 1, pageSize, updateUrl = null) => async (dispatch, getState) => {
  dispatch(setLoading(true));
  // Clear existing order items to avoid showing stale data while loading new results
  dispatch(setOrderItems([]));
  try {
    const { filters, pagination } = getState().orders;
    const params = new URLSearchParams();

    // Add pagination params, use pageSize from state if not provided
    const currentPageSize = pageSize || pagination.pageSize;
    params.append('page', page);
    params.append('pageSize', currentPageSize);

    // Always sort by newest first (purchaseDate DESC)
    params.append('sortBy', 'purchaseDate');
    params.append('sortOrder', 'DESC');

    // Add filters to API request for server-side filtering (more efficient)
    if (filters.searchText) {
      params.append('search', filters.searchText);
    }
    if (filters.selectedStatus && filters.selectedStatus !== 'all') {
      params.append('status', filters.selectedStatus);
    }
    if (filters.selectedDateRange && filters.selectedDateRange.length === 2) {
      const startDate = dayjs(filters.selectedDateRange[0]).format('YYYY-MM-DD');
      const endDate = dayjs(filters.selectedDateRange[1]).format('YYYY-MM-DD');
      params.append('startDate', startDate);
      params.append('endDate', endDate);
    }
    if (filters.selectedSellerAccount && filters.selectedSellerAccount !== 'all') {
      params.append('sellerAccountId', filters.selectedSellerAccount);
    }
    if (filters.selectedCustomizationStatus && filters.selectedCustomizationStatus !== 'all') {
      params.append('customizationStatus', filters.selectedCustomizationStatus);
    }
    if (filters.selectedPrinter && filters.selectedPrinter !== 'all') {
      params.append('printerId', filters.selectedPrinter);
    }
    if (filters.selectedFulfillmentStatus && filters.selectedFulfillmentStatus !== 'all') {
      params.append('fulfillmentStatus', filters.selectedFulfillmentStatus);
    }
    if (filters.selectedFulfillmentChannel && filters.selectedFulfillmentChannel !== 'all') {
      params.append('fulfillmentChannel', filters.selectedFulfillmentChannel);
    }
    if (filters.selectedShipmentStatus && filters.selectedShipmentStatus !== 'all') {
      params.append('shipmentStatus', filters.selectedShipmentStatus);
    }
    if (filters.selectedSupporter && filters.selectedSupporter !== 'all') {
      params.append('supporterId', filters.selectedSupporter);
    }
    if (filters.selectedCategory && filters.selectedCategory !== 'all') {
      params.append('categoryId', filters.selectedCategory);
    }
    if (filters.selectedMessageStatus && filters.selectedMessageStatus !== 'all') {
      params.append('messageStatus', filters.selectedMessageStatus);
    }
    if (filters.selectedShipDateDue && filters.selectedShipDateDue !== 'all') {
      params.append('shipDateDue', filters.selectedShipDateDue);
    }
    if (filters.selectedDeliveryDateDue && filters.selectedDeliveryDateDue !== 'all') {
      params.append('deliveryDateDue', filters.selectedDeliveryDateDue);
    }
    if (filters.selectedConfirmShipment && filters.selectedConfirmShipment !== 'all') {
      params.append('confirmShipment', filters.selectedConfirmShipment);
    }
    if (filters.selectedUpdatedLastMile && filters.selectedUpdatedLastMile !== 'all') {
      params.append('updatedLastMile', filters.selectedUpdatedLastMile);
    }
    if (filters.selectedMockupSentAt && filters.selectedMockupSentAt !== 'all') {
      params.append('mockupSentAt', filters.selectedMockupSentAt);
    }
    if (filters.selectedTitle && filters.selectedTitle.trim()) {
      params.append('title', filters.selectedTitle.trim());
    }
    if (filters.selectedSellerSku && filters.selectedSellerSku.trim()) {
      params.append('sellerSku', filters.selectedSellerSku.trim());
    }
    if (filters.selectedEmployeeId && filters.selectedEmployeeId.trim()) {
      params.append('employeeId', filters.selectedEmployeeId.trim());
    }
    if (filters.selectedExcludeTitle && filters.selectedExcludeTitle.trim()) {
      params.append('excludeTitle', filters.selectedExcludeTitle.trim());
    }
    if (filters.selectedExcludeSellerSku && filters.selectedExcludeSellerSku.trim()) {
      params.append('excludeSellerSku', filters.selectedExcludeSellerSku.trim());
    }

    const response = await api.get(`/orders/items?${params.toString()}`);
    if (response.data) {
      // Sort the items by sellerId first, then by purchaseDate, then by amazonOrderId for consistent ordering
      const sortedItems = response.data.items.sort((a, b) => {
        // First sort by sellerId (ascending)
        const sellerIdA = a.order?.sellerId || '';
        const sellerIdB = b.order?.sellerId || '';
        if (sellerIdA !== sellerIdB) {
          return sellerIdA.localeCompare(sellerIdB);
        }
        
        // Second sort by purchaseDate (YYYY-MM-DD format, descending)
        const purchaseDateA = a.order?.purchaseDate ? dayjs(a.order.purchaseDate).format('YYYY-MM-DD') : '';
        const purchaseDateB = b.order?.purchaseDate ? dayjs(b.order.purchaseDate).format('YYYY-MM-DD') : '';
        if (purchaseDateA !== purchaseDateB) {
          return purchaseDateB.localeCompare(purchaseDateA);
        }
        
        // Finally sort by amazonOrderId (ascending)
        const amazonOrderIdA = a.amazonOrderId || '';
        const amazonOrderIdB = b.amazonOrderId || '';
        return amazonOrderIdA.localeCompare(amazonOrderIdB);
      });
      
      dispatch(setOrderItems(sortedItems));
      const newPagination = {
        current: parseInt(response.data.pagination.page),
        pageSize: parseInt(response.data.pagination.pageSize),
        total: parseInt(response.data.pagination.total)
      };
      dispatch(setPagination(newPagination));
      // Update URL if updateUrl function is provided
      if (updateUrl) {
        updateUrl(filters, newPagination);
      }
    }
  } catch (error) {
    console.error('Error fetching order items:', error);
    dispatch(setError('Không thể lấy danh sách các mục đơn hàng'));
  } finally {
    dispatch(setLoading(false));
  }
};

// Individual filter handlers that only update state - no API calls
export const handleSearchChange = (searchText) => (dispatch, getState) => {
  const { filters } = getState().orders;
  dispatch(setFilters({ ...filters, searchText }));
};

export const handleStatusChange = (selectedStatus) => (dispatch, getState) => {
  const { filters } = getState().orders;
  dispatch(setFilters({ ...filters, selectedStatus }));
};

export const handleDateRangeChange = (selectedDateRange) => (dispatch, getState) => {
  const { filters } = getState().orders;
  dispatch(setFilters({ ...filters, selectedDateRange }));
};

export const handleSellerAccountChange = (selectedSellerAccount) => (dispatch, getState) => {
  const { filters } = getState().orders;
  dispatch(setFilters({ ...filters, selectedSellerAccount }));
};

export const handleCustomizationStatusChange = (selectedCustomizationStatus) => (dispatch, getState) => {
  const { filters } = getState().orders;
  dispatch(setFilters({ ...filters, selectedCustomizationStatus }));
};

export const handlePrinterChange = (selectedPrinter) => (dispatch, getState) => {
  const { filters } = getState().orders;
  dispatch(setFilters({ ...filters, selectedPrinter }));
};

export const handleFulfillmentStatusChange = (selectedFulfillmentStatus) => (dispatch, getState) => {
  const { filters } = getState().orders;
  dispatch(setFilters({ ...filters, selectedFulfillmentStatus }));
};

export const handleFulfillmentChannelChange = (selectedFulfillmentChannel) => (dispatch, getState) => {
  const { filters } = getState().orders;
  dispatch(setFilters({ ...filters, selectedFulfillmentChannel }));
};

export const handleShipmentStatusChange = (selectedShipmentStatus) => (dispatch, getState) => {
  const { filters } = getState().orders;
  dispatch(setFilters({ ...filters, selectedShipmentStatus }));
};

export const handleSupporterChange = (selectedSupporter) => (dispatch, getState) => {
  const { filters } = getState().orders;
  dispatch(setFilters({ ...filters, selectedSupporter }));
};

export const handleCategoryChange = (selectedCategory) => (dispatch, getState) => {
  const { filters } = getState().orders;
  dispatch(setFilters({ ...filters, selectedCategory }));
};

export const handleMessageStatusChange = (selectedMessageStatus) => (dispatch, getState) => {
  const { filters } = getState().orders;
  dispatch(setFilters({ ...filters, selectedMessageStatus }));
};

export const handleShipDateDueChange = (selectedShipDateDue) => (dispatch, getState) => {
  const { filters } = getState().orders;
  dispatch(setFilters({ ...filters, selectedShipDateDue }));
};

export const handleDeliveryDateDueChange = (selectedDeliveryDateDue) => (dispatch, getState) => {
  const { filters } = getState().orders;
  dispatch(setFilters({ ...filters, selectedDeliveryDateDue }));
};

export const handleConfirmShipmentChange = (selectedConfirmShipment) => (dispatch, getState) => {
  const { filters } = getState().orders;
  dispatch(setFilters({ ...filters, selectedConfirmShipment }));
};

export const handleUpdatedLastMileChange = (selectedUpdatedLastMile) => (dispatch, getState) => {
  const { filters } = getState().orders;
  dispatch(setFilters({ ...filters, selectedUpdatedLastMile }));
};

export const handleMockupSentAtChange = (selectedMockupSentAt) => (dispatch, getState) => {
  const { filters } = getState().orders;
  dispatch(setFilters({ ...filters, selectedMockupSentAt }));
};

export const handleTitleChange = (selectedTitle) => (dispatch, getState) => {
  const { filters } = getState().orders;
  dispatch(setFilters({ ...filters, selectedTitle }));
};

export const handleSellerSkuChange = (selectedSellerSku) => (dispatch, getState) => {
  const { filters } = getState().orders;
  dispatch(setFilters({ ...filters, selectedSellerSku }));
};

export const handleEmployeeIdChange = (selectedEmployeeId) => (dispatch, getState) => {
  const { filters } = getState().orders;
  dispatch(setFilters({ ...filters, selectedEmployeeId }));
};

export const handleExcludeTitleChange = (selectedExcludeTitle) => (dispatch, getState) => {
  const { filters } = getState().orders;
  dispatch(setFilters({ ...filters, selectedExcludeTitle }));
};

export const handleExcludeSellerSkuChange = (selectedExcludeSellerSku) => (dispatch, getState) => {
  const { filters } = getState().orders;
  dispatch(setFilters({ ...filters, selectedExcludeSellerSku }));
};

export const handleClearFilters = (updateUrl = null) => (dispatch) => {
  dispatch(clearFilters());
  // Reset to first page and clear total to avoid showing stale pagination info  
  const newPagination = { current: 1, total: 0 };
  dispatch(setPagination(newPagination));

  // Update URL if updateUrl function is provided
  if (updateUrl) {
    const clearedFilters = {
      searchText: '',
      selectedStatus: 'all',
      selectedDateRange: null,
      selectedSellerAccount: 'all',
      selectedCustomizationStatus: 'all',
      selectedPrinter: 'all',
      selectedFulfillmentStatus: 'all',
      selectedFulfillmentChannel: 'all',
      selectedShipmentStatus: 'all',
      selectedSupporter: 'all',
      selectedCategory: 'all',
      selectedMessageStatus: 'all',
      selectedShipDateDue: 'all',
      selectedDeliveryDateDue: 'all',
      selectedConfirmShipment: 'all',
      selectedUpdatedLastMile: 'all',
      selectedMockupSentAt: 'all',
      selectedTitle: '',
      selectedSellerSku: '',
      selectedEmployeeId: '',
      selectedExcludeTitle: '',
      selectedExcludeSellerSku: ''
    };
    updateUrl(clearedFilters, newPagination);
  }

  dispatch(fetchOrderItems(1));
  // Also fetch KPI metrics with cleared filters
  dispatch(fetchKpiMetrics());
};

// Improved filter function that only updates state, does NOT call API
export const updateFilters = (filterData) => (dispatch) => {
  // Only update filters in state, don't fetch data
  dispatch(setFilters(filterData));
};

// Separate function to apply filters and fetch data
export const applyFilters = (updateUrl = null) => (dispatch, getState) => {
  const { pagination } = getState().orders;
  // Reset to first page and clear total, but keep current pageSize
  const newPagination = { current: 1, total: 0, pageSize: pagination.pageSize };
  dispatch(setPagination(newPagination));

  // Update URL if updateUrl function is provided
  if (updateUrl) {
    const { filters } = getState().orders;
    updateUrl(filters, newPagination);
  }

  dispatch(fetchOrderItems(1, pagination.pageSize, updateUrl));
  // Also fetch KPI metrics with the new filters
  dispatch(fetchKpiMetrics());

  // Log current filters for debugging
  console.log('Applied filters:', getState().orders.filters);
};

// Updated pagination handler to support URL updates
export const handlePaginationChange = (page, pageSize, updateUrl = null) => (dispatch, getState) => {
  const newPagination = { current: page, pageSize };
  dispatch(setPagination(newPagination));

  // Update URL if updateUrl function is provided
  if (updateUrl) {
    const { filters } = getState().orders;
    updateUrl(filters, newPagination);
  }

  dispatch(fetchOrderItems(page, pageSize, updateUrl));
};

// Hàm xem chi tiết đơn hàng
export const viewOrderDetail = (orderId, navigate) => () => {
  navigate(`/orders/${orderId}`);
};

// Hàm chỉnh sửa đơn hàng
export const editOrder = (orderId, navigate) => () => {
  navigate(`/orders/${orderId}/edit`);
};

// Hàm xóa đơn hàng
export const deleteOrder = (orderId) => async (dispatch) => {
  try {
    await api.delete(`/orders/${orderId}`);
    // Refresh data after deletion
    dispatch(fetchOrderItems());
  } catch (error) {
    console.error('Error deleting order:', error);
    dispatch(setError('Không thể xóa đơn hàng'));
  }
};

// Đồng bộ đơn hàng từ Amazon
export const syncAmazonOrders = (params) => async (dispatch) => {
  try {
    const response = await api.post('/orders/sync-amazon', params);
    if (response.data.success) {
      // Refresh data after sync while preserving pagination
      dispatch(fetchOrderItems());
    }
    return response.data;
  } catch (error) {
    console.error('Error syncing orders:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Có lỗi xảy ra khi đồng bộ đơn hàng'
    };
  }
};

// Đồng bộ dữ liệu tài chính từ Amazon
export const syncFinancialData = (params) => async (dispatch) => {
  try {
    const response = await api.post('/orders/sync-financial', params);
    if (response.data.success) {
      // Refresh data after sync while preserving pagination
      dispatch(fetchOrderItems());
    }
    return response.data;
  } catch (error) {
    console.error('Error syncing financial data:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Có lỗi xảy ra khi đồng bộ dữ liệu tài chính'
    };
  }
};

// Gửi mockup cho khách hàng
export const sendMockup = (orderItemIds, pagination = null) => async (dispatch) => {
  try {
    dispatch(setLoading(true));

    const response = await api.post('/orders/send-mockup', {
      orderItemIds
    }, {
      timeout: 60000 // 1 minute - chỉ cần tạo jobs
    });

    if (response.data.success) {
      const { totalOrders, jobsCreated, jobsFailed } = response.data;
      
      // Refresh data after sending mockup request
      if (pagination) {
        dispatch(fetchOrderItems(pagination.current, pagination.pageSize));
      } else {
        dispatch(fetchOrderItems());
      }

      // Show detailed message
      const createdCount = jobsCreated || 0;
      const failedCount = jobsFailed || 0;
      const totalCount = totalOrders || 0;
      
      let message = `Đã tạo ${createdCount} jobs để gửi mockup`;
      if (failedCount > 0) {
        message += `. ${failedCount} jobs tạo thất bại`;
      }
      message += '. Đang xử lý trong background...';

      return {
        success: true,
        message,
        totalOrders: totalCount,
        jobsCreated: createdCount,
        jobsFailed: failedCount
      };
    } else {
      return {
        success: false,
        message: response.data.message || 'Có lỗi xảy ra khi gửi mockup'
      };
    }
  } catch (error) {
    console.error('Error sending mockup:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Có lỗi xảy ra khi gửi mockup'
    };
  } finally {
    dispatch(setLoading(false));
  }
};

// Gửi follow-up message cho khách hàng
export const sendFollowUpMessage = (orderItemIds, pagination = null) => async (dispatch) => {
  try {
    dispatch(setLoading(true));

    const response = await api.post('/orders/send-follow-up-message', {
      orderItemIds
    });

    if (response.data.success) {
      // Refresh data after sending follow-up message while preserving pagination
      if (pagination) {
        dispatch(fetchOrderItems(pagination.current, pagination.pageSize));
      } else {
        dispatch(fetchOrderItems());
      }
      return {
        success: true,
        message: response.data.message || 'Đã gửi tin nhắn nhắc nhở thành công'
      };
    } else {
      return {
        success: false,
        message: response.data.message || 'Có lỗi xảy ra khi gửi tin nhắn nhắc nhở'
      };
    }
  } catch (error) {
    console.error('Error sending follow-up message:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Có lỗi xảy ra khi gửi tin nhắn nhắc nhở'
    };
  } finally {
    dispatch(setLoading(false));
  }
};

// Confirm shipment cho các order items
export const confirmShipment = (orderItemIds, pagination = null) => async (dispatch) => {
  try {
    dispatch(setLoading(true));

    const response = await api.post('/orders/confirm-shipment', {
      orderItemIds
    });

    if (response.data.success) {
      // Wait a bit for backend to process each order item (500ms per item, minimum 1s, maximum 5s)
      const delayMs = Math.max(orderItemIds.length * 500, 1000);
      await new Promise(resolve => setTimeout(resolve, delayMs));

      // Refresh data after confirming shipment to update status while preserving pagination
      if (pagination) {
        dispatch(fetchOrderItems(pagination.current, pagination.pageSize));
      } else {
        dispatch(fetchOrderItems());
      }
      return {
        success: true,
        message: response.data.message || 'Đã xác nhận shipment thành công'
      };
    } else {
      return {
        success: false,
        message: response.data.message || 'Có lỗi xảy ra khi xác nhận shipment'
      };
    }
  } catch (error) {
    console.error('Error confirming shipment:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Có lỗi xảy ra khi xác nhận shipment'
    };
  } finally {
    dispatch(setLoading(false));
  }
};

// Update message process status
export const updateMessageProcessStatus = (messageId, isProcessed) => async (dispatch) => {
  try {
    const response = await api.patch(`/orders/messages/${messageId}/process-status`, {
      isProcessed
    });

    if (response.data.success) {
      return {
        success: true,
        message: response.data.message || 'Message status updated successfully',
        data: response.data.data
      };
    } else {
      return {
        success: false,
        message: response.data.message || 'Failed to update message status'
      };
    }
  } catch (error) {
    console.error('Error updating message process status:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Error occurred while updating message status'
    };
  }
};

// Send message to customer
export const sendCustomerMessage = ({ orderId, amazonOrderId, messageContent }) => async (dispatch) => {
  try {
    const response = await api.post('/orders/send-customer-message', {
      orderId,
      amazonOrderId,
      messageContent
    });

    if (response.data.success) {
      return {
        success: true,
        message: response.data.message || 'Message sent successfully',
        data: response.data.data
      };
    } else {
      return {
        success: false,
        message: response.data.message || 'Failed to send message'
      };
    }
  } catch (error) {
    console.error('Error sending customer message:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Error occurred while sending message'
    };
  }
};

// Send bulk message to customers
export const sendBulkMessage = ({ orderItemIds, messageContent }) => async (dispatch) => {
  try {
    const response = await api.post('/orders/send-bulk-message', {
      orderItemIds,
      messageContent
    });

    if (response.data.success) {
      return {
        success: true,
        message: response.data.message || 'Bulk message sent successfully',
        data: response.data.data
      };
    } else {
      return {
        success: false,
        message: response.data.message || 'Failed to send bulk message'
      };
    }
  } catch (error) {
    console.error('Error sending bulk message:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Error occurred while sending bulk message'
    };
  }
};

// Bulk update order items
export const bulkUpdateOrderItems = (orderItemIds, updateData) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const response = await api.patch('/orders/items/bulk-update', {
      orderItemIds,
      updateData
    });

    if (response.data.success) {
      return {
        success: true,
        message: response.data.message || `Successfully updated ${orderItemIds.length} order items`,
        data: response.data.data
      };
    } else {
      return {
        success: false,
        message: response.data.message || 'Failed to update order items'
      };
    }
  } catch (error) {
    console.error('Error bulk updating order items:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Error occurred while updating order items'
    };
  } finally {
    dispatch(setLoading(false));
  }
};

// Upload order report CSV
export const uploadOrderReport = (formData) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const response = await api.post('/orders/upload-report', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    if (response.data.success) {
      // Refresh order items after successful upload
      dispatch(fetchOrderItems());
      return {
        success: true,
        message: response.data.message || 'Order report uploaded and processed successfully',
        data: response.data.data
      };
    } else {
      return {
        success: false,
        message: response.data.message || 'Failed to upload order report'
      };
    }
  } catch (error) {
    console.error('Error uploading order report:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Error occurred while uploading order report'
    };
  } finally {
    dispatch(setLoading(false));
  }
};

// Trigger sync Amazon Order Reports for specific seller account
export const triggerReportSync = (sellerAccountId) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const response = await api.post('/orders/trigger-report-sync', {
      sellerAccountId
    });

    if (response.data.success) {
      return {
        success: true,
        message: response.data.message || 'Amazon Order Reports sync triggered successfully',
        data: response.data.data
      };
    } else {
      return {
        success: false,
        message: response.data.message || 'Failed to trigger sync Amazon Order Reports'
      };
    }
  } catch (error) {
    console.error('Error triggering report sync:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Error occurred while triggering sync'
    };
  } finally {
    dispatch(setLoading(false));
  }
};

// Trigger order processing temp job
export const triggerOrderProcessing = () => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const response = await api.post('/orders/trigger-order-processing');

    if (response.data.success) {
      return {
        success: true,
        message: response.data.message || 'Order processing job triggered successfully'
      };
    } else {
      return {
        success: false,
        message: response.data.message || 'Failed to trigger order processing job'
      };
    }
  } catch (error) {
    console.error('Error triggering order processing:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Error occurred while triggering order processing'
    };
  } finally {
    dispatch(setLoading(false));
  }
};

// Fetch KPI metrics based on current filters
export const fetchKpiMetrics = () => async (dispatch, getState) => {
  dispatch(setLoading(true));
  try {
    const { filters } = getState().orders;
    const params = new URLSearchParams();
    console.log('PARAMS:', filters);
    // Add filters to API request
    if (filters.searchText) {
      params.append('search', filters.searchText);
    }
    if (filters.selectedStatus && filters.selectedStatus !== 'all') {
      params.append('status', filters.selectedStatus);
    }
    if (filters.selectedDateRange && filters.selectedDateRange.length === 2) {
      const startDate = dayjs(filters.selectedDateRange[0]).format('YYYY-MM-DD');
      const endDate = dayjs(filters.selectedDateRange[1]).format('YYYY-MM-DD');
      params.append('startDate', startDate);
      params.append('endDate', endDate);
    }
    if (filters.selectedSellerAccount && filters.selectedSellerAccount !== 'all') {
      params.append('sellerAccountId', filters.selectedSellerAccount);
    }
    if (filters.selectedCustomizationStatus && filters.selectedCustomizationStatus !== 'all') {
      params.append('customizationStatus', filters.selectedCustomizationStatus);
    }
    if (filters.selectedPrinter && filters.selectedPrinter !== 'all') {
      params.append('printerId', filters.selectedPrinter);
    }
    if (filters.selectedFulfillmentStatus && filters.selectedFulfillmentStatus !== 'all') {
      params.append('fulfillmentStatus', filters.selectedFulfillmentStatus);
    }
    if (filters.selectedFulfillmentChannel && filters.selectedFulfillmentChannel !== 'all') {
      params.append('fulfillmentChannel', filters.selectedFulfillmentChannel);
    }
    if (filters.selectedShipmentStatus && filters.selectedShipmentStatus !== 'all') {
      params.append('shipmentStatus', filters.selectedShipmentStatus);
    }
    if (filters.selectedSupporter && filters.selectedSupporter !== 'all') {
      params.append('supporterId', filters.selectedSupporter);
    }
    if (filters.selectedCategory && filters.selectedCategory !== 'all') {
      params.append('categoryId', filters.selectedCategory);
    }
    if (filters.selectedMessageStatus && filters.selectedMessageStatus !== 'all') {
      params.append('messageStatus', filters.selectedMessageStatus);
    }
    if (filters.selectedShipDateDue && filters.selectedShipDateDue !== 'all') {
      params.append('shipDateDue', filters.selectedShipDateDue);
    }
    if (filters.selectedDeliveryDateDue && filters.selectedDeliveryDateDue !== 'all') {
      params.append('deliveryDateDue', filters.selectedDeliveryDateDue);
    }
    if (filters.selectedConfirmShipment && filters.selectedConfirmShipment !== 'all') {
      params.append('confirmShipment', filters.selectedConfirmShipment);
    }
    if (filters.selectedUpdatedLastMile && filters.selectedUpdatedLastMile !== 'all') {
      params.append('updatedLastMile', filters.selectedUpdatedLastMile);
    }
    if (filters.selectedMockupSentAt && filters.selectedMockupSentAt !== 'all') {
      params.append('mockupSentAt', filters.selectedMockupSentAt);
    }
    if (filters.selectedTitle && filters.selectedTitle.trim()) {
      params.append('title', filters.selectedTitle.trim());
    }
    if (filters.selectedSellerSku && filters.selectedSellerSku.trim()) {
      params.append('sellerSku', filters.selectedSellerSku.trim());
    }
    if (filters.selectedEmployeeId && filters.selectedEmployeeId.trim()) {
      params.append('employeeId', filters.selectedEmployeeId.trim());
    }
    if (filters.selectedExcludeTitle && filters.selectedExcludeTitle.trim()) {
      params.append('excludeTitle', filters.selectedExcludeTitle.trim());
    }
    if (filters.selectedExcludeSellerSku && filters.selectedExcludeSellerSku.trim()) {
      params.append('excludeSellerSku', filters.selectedExcludeSellerSku.trim());
    }

    const response = await api.get(`/orders/kpi-metrics?${params.toString()}`);

    if (response.data && response.data.success) {
      dispatch(setKpiMetrics(response.data.data));
      return response.data.data;
    } else {
      console.error('Error fetching KPI metrics:', response.data?.message);
      dispatch(setKpiMetrics(null));
      return null;
    }
  } catch (error) {
    console.error('Error fetching KPI metrics:', error);
    dispatch(setKpiMetrics(null));
    return null;
  } finally {
    dispatch(setLoading(false));
  }
};